import { useState } from 'react';
import type { ReactElement } from 'react';
import { paisa } from '@fleet/shared';
import { useRevenueQuery } from '@/api/endpoints';
import { Button } from '@/design/Button';
import { ColumnChart, ShareBars } from '@/design/Chart';
import { Figure, MoneyText } from '@/design/Data';
import { EmptyState, ErrorState } from '@/design/EmptyState';
import { Panel } from '@/design/Panel';
import { distinctVehicleNames } from '@/features/fleet/labels';

/**
 * Revenue.
 *
 * **Money that arrived**, not money that was promised. A booking total is a
 * hope until somebody pays it, so every figure on this screen comes from
 * payments — grouped by when the cash was *received*, which is why back-dating
 * a payment matters and why the field exists.
 *
 * The fiscal year is the Nepali one, Shrawan to Ashadh, because that is the
 * year the accountant closes the books on.
 *
 * The months are drawn as bars rather than plotted with a charting library. Two
 * reasons: twelve numbers do not need 90KB of JavaScript on a phone on mobile
 * data, and a bar whose length is a percentage of the largest month is a chart
 * a person can read without a legend.
 */

/** "17 Jul 2026" — both ends of the fiscal year are read as dates, not months. */
function dayAndMonth(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

interface MonthRow {
  readonly month: string;
  readonly title: string;
  readonly taken: number;
  readonly bookings: number;
}

export function RevenuePage(): ReactElement {
  /**
   * Which fiscal year is on screen. Undefined means the one in progress.
   *
   * QA reported that there was no way to look at anything but now: the report
   * was hard-wired to the current year, so on Shrawan 1 the previous twelve
   * months became unreachable from the product even though the data was all
   * still there.
   */
  const [year, setYear] = useState<number | undefined>(undefined);
  const [asTable, setAsTable] = useState(false);
  const { data, isLoading, isError, refetch } = useRevenueQuery(year);

  if (isError) {
    return (
      <ErrorState
        headline="Revenue did not load"
        detail="The server did not answer. Check the connection and load it again."
        onRetry={() => void refetch()}
        retryLabel="Load again"
      />
    );
  }

  if (isLoading || data === undefined) {
    return <div className="h-64" aria-busy="true" />;
  }

  const months: readonly MonthRow[] = data.by_month;
  const vehicleLabels = distinctVehicleNames(
    data.by_vehicle
      .filter((row) => row.vehicle !== null)
      .map((row) => ({ id: row.vehicle ?? '', name: row.name, plate: row.plate })),
  );
  const busiest = months.reduce((most, month) => Math.max(most, month.taken), 0);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-display font-semibold">Revenue</h1>

        {/* Named by its fiscal year, which is what the accountant asks for,
            with the Gregorian dates underneath because that is what everyone
            else counts in. Forward is disabled on the current year: there is
            no revenue in a year that has not started. */}
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => setYear(data.year - 1)}>Earlier year</Button>
          <span className="text-small text-ink-muted">
            Fiscal year <span className="tnum font-medium text-ink">{data.fiscal_year}</span> ·{' '}
            {dayAndMonth(data.from_date)} to {dayAndMonth(data.to_date)}
          </span>
          <Button disabled={data.is_current_year} onClick={() => setYear(data.year + 1)}>
            Later year
          </Button>
          <Button disabled={data.is_current_year} onClick={() => setYear(undefined)}>
            This year
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Figure
          label={data.is_current_year ? 'Taken this year' : `Taken in ${data.fiscal_year}`}
          value={<MoneyText amount={paisa(data.taken)} />}
        />
        <Figure label="Invoiced" value={<MoneyText amount={paisa(data.invoiced)} />} />
        {/* Always "as of right now", never as of the year being looked at, so
            it is only shown where that is what a reader would assume. */}
        {data.is_current_year && (
          <Figure
            label="Due"
            value={<MoneyText amount={paisa(data.outstanding)} />}
            emphasis={data.outstanding > 0 ? 'attention' : 'neutral'}
          />
        )}
      </div>

      <Panel
        title="Month by month"
        note="Counted in the month of the trip — an advance for a December booking is December revenue"
        current
        actions={
          <Button onClick={() => setAsTable((was) => !was)}>
            {asTable ? 'Show the chart' : 'Show the numbers'}
          </Button>
        }
      >
        {/* Every month the year touches is here, empty ones included — a quiet
            month is a fact, and a missing column reads as a half-loaded
            screen. So "nothing here" means nothing arrived all year. */}
        {busiest === 0 ? (
          <EmptyState
            headline={
              data.is_current_year
                ? 'No payments recorded yet'
                : `Nothing was taken in ${data.fiscal_year}`
            }
            detail="Take a payment on a booking and this fills in. Until then there is nothing to total — a booking's price is not revenue."
          />
        ) : asTable ? (
          /* The same numbers as a table. Some people want to read the figures
             rather than the shape, and a chart with no table behind it is a
             chart that excludes them. */
          <table className="w-full text-base">
            <thead>
              <tr className="border-b border-rule text-left text-micro uppercase text-ink-muted">
                <th className="px-3 py-2 font-medium sm:px-4">Month</th>
                <th className="px-3 py-2 text-right font-medium sm:px-4">Bookings</th>
                <th className="px-3 py-2 text-right font-medium sm:px-4">Taken</th>
              </tr>
            </thead>
            <tbody>
              {months.map((month) => (
                <tr key={month.month} className="border-b border-rule last:border-b-0">
                  <td className="px-3 py-2 sm:px-4">{month.title}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-ink-muted sm:px-4">
                    {month.bookings}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium sm:px-4">
                    <MoneyText amount={paisa(month.taken)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <ColumnChart
            columns={months.map((month) => ({
              key: month.month,
              // "Sep", not "Sep 2026": thirteen columns of the long form do
              // not fit on a counter screen, and the year is in the heading.
              label: month.title.split(' ')[0] ?? month.title,
              value: month.taken,
              caption: `${String(month.bookings)} ${month.bookings === 1 ? 'booking' : 'bookings'}`,
            }))}
            format={(value) => <MoneyText amount={paisa(value)} />}
            description={`Money taken each month of fiscal year ${data.fiscal_year}`}
          />
        )}
      </Panel>

      <Panel
        title="Which vehicles earned it"
        note="Payments attributed to the vehicle on the booking"
      >
        {data.by_vehicle.length === 0 ? (
          <EmptyState
            headline="Nothing to attribute yet"
            detail="Once payments are recorded, this shows which vehicles brought the money in."
          />
        ) : (
          /* A ranked list of bars rather than a table. The question this panel
             answers is "which cars are earning and which are sitting", and
             that is a shape, not a column of figures — the numbers are still
             on every row for anyone who wants them. */
          <ShareBars
            rows={data.by_vehicle.map((row) => ({
              key: row.vehicle ?? row.plate,
              label: vehicleLabels.get(row.vehicle ?? '') ?? row.name,
              identifier: true,
              caption: `${row.name} · ${String(row.trips)} ${row.trips === 1 ? 'trip' : 'trips'}`,
              value: row.taken,
              showShare: true,
            }))}
            format={(value) => <MoneyText amount={paisa(value)} />}
          />
        )}
      </Panel>

      <Panel title="How it arrived" note="Useful when reconciling the till against the bank">
        {data.by_method.length === 0 ? (
          <EmptyState
            headline="No payments yet"
            detail="Cash, Fonepay, card and cheque totals appear here as payments are recorded."
          />
        ) : (
          <ShareBars
            rows={data.by_method.map((method) => ({
              key: method.method,
              label: method.title,
              value: method.taken,
              showShare: true,
            }))}
            format={(value) => <MoneyText amount={paisa(value)} />}
          />
        )}
      </Panel>

    </div>
  );
}
